/**
 * Intercepts expired/invalid session errors from any API response and redirects
 * the user to the login page exactly once, clearing the stored access token.
 */

import axios from 'axios';

import { appRoutes, saveAuthReturnPath } from '@/app/routes';
import { clearAccessToken } from '@/utils/authToken';
import { createLogger } from '@/utils/logging';

const logger = createLogger('SessionExpiry', 'api');

/** Prevents duplicate redirects when multiple in-flight requests all fail with 401. */
let redirectInFlight = false;

/** Matches known expiry phrases across backend error bodies (case-insensitive). */
const EXPIRED_TEXT_PATTERN =
  /(session token is expired|token expired|expired token|invalid or expired|expired bearer token|jwt expired)/i;

/**
 * Walks a backend error payload and collects every human-readable message string it contains.
 *
 * @param payload - The raw `error.response.data` value from an Axios error.
 * @returns An array of candidate message strings to match against expiry patterns.
 */
const collectErrorMessages = (payload: unknown): string[] => {
  const messages: string[] = [];

  if (typeof payload === 'string') {
    messages.push(payload);
    return messages;
  }

  if (!payload || typeof payload !== 'object') {
    return messages;
  }

  const objectPayload = payload as Record<string, unknown>;

  const detail = objectPayload.detail;
  if (typeof detail === 'string') {
    messages.push(detail);
  } else if (detail && typeof detail === 'object') {
    const nested = detail as Record<string, unknown>;
    if (typeof nested.detail === 'string') {
      messages.push(nested.detail);
    }
    if (typeof nested.message === 'string') {
      messages.push(nested.message);
    }
  }

  if (typeof objectPayload.message === 'string') {
    messages.push(objectPayload.message);
  }

  if (typeof objectPayload.error === 'string') {
    messages.push(objectPayload.error);
  }

  return messages;
};

/**
 * Returns true when the given error represents an expired or invalid authentication token.
 * Matches on HTTP 401 status and a set of known expiry message patterns.
 *
 * @param error - Any caught value, typically an AxiosError.
 */
export const isSessionExpiredError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const statusCode = error.response?.status;
  const responseMessages = collectErrorMessages(error.response?.data);
  const fallbackMessage = error.message;
  const mergedMessages = [...responseMessages, fallbackMessage].filter((value): value is string =>
    Boolean(value && value.trim().length > 0),
  );

  if (statusCode === 401) {
    return true;
  }

  return mergedMessages.some((message) => EXPIRED_TEXT_PATTERN.test(message));
};

/**
 * Clears the access token and redirects to the login page when a session-expiry error is detected.
 * No-ops if the error is not an expiry, if a redirect is already in progress, or if already on the
 * login page.
 *
 * @param error - Any caught value; only acts when `isSessionExpiredError` returns true.
 */
export const handleSessionExpired = (error: unknown): void => {
  if (!isSessionExpiredError(error) || redirectInFlight) {
    return;
  }

  redirectInFlight = true;
  saveAuthReturnPath();
  clearAccessToken();
  logger.warn('Detected expired/invalid session token. Redirecting to login.');

  if (globalThis.location.pathname === appRoutes.login) {
    redirectInFlight = false;
    return;
  }

  globalThis.location.assign(appRoutes.login);
};
